import "../style.css";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRightIcon, CheckRoundedIcon, EmailIcon } from "../../../../components/icons";
import { Stagger, StaggerItem, pressable } from "../../../../components/motion";

// The four screens of the email-verification page. Wording and icons are the
// page's originals; only layout, colours and motion are new.

const GOLD = "#DBBE78";
const TITLE_CLASS =
    "tw:!m-0 tw:!font-libre-caslon-display tw:!text-[24px] tw:sm:!text-[28px] tw:!font-[400] tw:!leading-snug tw:!text-[#282828] tw:!text-center";
const GOLD_BUTTON_CLASS =
    "tw:inline-flex tw:w-full tw:items-center tw:justify-center tw:gap-2 tw:rounded tw:bg-[#DBBE78] tw:px-10 tw:py-3 tw:text-[14px] tw:font-bold tw:!text-white tw:!no-underline tw:transition-colors tw:hover:bg-[#CFAF62] tw:sm:w-auto tw:sm:min-w-[240px]";

// Translation strings carry <b>/<a> markup, so they render as HTML.
const HtmlText = ({ as: Tag = "p", translationKey, className }) => {
    const { t } = useTranslation();
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: t(translationKey) }} />;
};

// ─── Loading ─────────────────────────────────────────────────────────────────

// Gold ring turning around the envelope while the link is being checked.
export const VerifyingState = () => {
    const { t } = useTranslation();
    const shouldReduce = useReducedMotion();

    return (
        <div role="status" aria-live="polite" className="tw:flex tw:flex-col tw:items-center tw:gap-6 tw:py-10">
            <div className="tw:relative tw:flex tw:h-20 tw:w-20 tw:items-center tw:justify-center">
                <span className="tw:absolute tw:inset-0 tw:rounded-full tw:border-2 tw:border-[#F1E4C3]" />
                <motion.span
                    className="tw:absolute tw:inset-0 tw:rounded-full tw:border-2 tw:border-transparent"
                    style={{ borderTopColor: GOLD, borderRightColor: GOLD }}
                    animate={shouldReduce ? { opacity: [0.4, 1, 0.4] } : { rotate: 360 }}
                    transition={{ duration: shouldReduce ? 1.4 : 1, ease: "linear", repeat: Infinity }}
                    aria-hidden="true"
                />
                <motion.span
                    className="tw:flex"
                    animate={shouldReduce ? undefined : { scale: [1, 1.08, 1] }}
                    transition={{ duration: 1.6, ease: "easeInOut", repeat: Infinity }}
                    aria-hidden="true"
                >
                    <EmailIcon size={28} />
                </motion.span>
            </div>
            <motion.p
                className="tw:!m-0 tw:text-[15px] tw:text-[#4E4E4E]"
                animate={shouldReduce ? undefined : { opacity: [0.55, 1, 0.55] }}
                transition={{ duration: 1.8, ease: "easeInOut", repeat: Infinity }}
            >
                {t("emailVerification.verifying")}
            </motion.p>
        </div>
    );
};

// ─── Success ─────────────────────────────────────────────────────────────────

// The original check mark: springs in over a soft halo, then stays "alive" —
// teal rings ripple outward and the mark gently breathes in time with them.
const RIPPLE_SECONDS = 2.6;
const RIPPLE_COUNT = 2;

const RippleRing = ({ index }) => (
    <motion.span
        className="tw:absolute tw:inset-0 tw:rounded-full tw:border tw:border-[#3CAEA3]/40"
        initial={{ scale: 1, opacity: 0 }}
        animate={{ scale: [1, 1.6], opacity: [0.55, 0] }}
        transition={{
            duration: RIPPLE_SECONDS,
            ease: "easeOut",
            repeat: Infinity,
            // Start once the entrance has settled; rings are evenly spaced.
            delay: 0.8 + (index * RIPPLE_SECONDS) / RIPPLE_COUNT,
        }}
        aria-hidden="true"
    />
);

const SuccessMark = () => {
    const shouldReduce = useReducedMotion();
    return (
        // Vertical padding leaves room for the rings, which grow past the icon.
        <StaggerItem className="tw:mb-2 tw:flex tw:justify-center tw:py-6">
            <div className="tw:relative tw:flex tw:h-[84px] tw:w-[84px] tw:items-center tw:justify-center">
                {!shouldReduce &&
                    Array.from({ length: RIPPLE_COUNT }, (_, index) => <RippleRing key={index} index={index} />)}
                <motion.span
                    className="tw:absolute tw:inset-0 tw:rounded-full tw:bg-[#3CAEA3]/10"
                    initial={{ scale: shouldReduce ? 1 : 0.4, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    aria-hidden="true"
                />
                {/* Outer span: one-time entrance. Inner span: the endless breathing. */}
                <motion.span
                    className="tw:relative tw:flex"
                    initial={{ scale: shouldReduce ? 1 : 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 380, damping: 18, delay: 0.1 }}
                >
                    <motion.span
                        className="tw:flex"
                        animate={shouldReduce ? undefined : { scale: [1, 1.06, 1] }}
                        transition={{
                            duration: RIPPLE_SECONDS / RIPPLE_COUNT,
                            ease: "easeInOut",
                            repeat: Infinity,
                            delay: 0.8,
                        }}
                    >
                        <CheckRoundedIcon size={56} />
                    </motion.span>
                </motion.span>
            </div>
        </StaggerItem>
    );
};

export const BusinessVerified = () => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Stagger onMount stagger={0.08} className="tw:flex tw:flex-col tw:items-center tw:text-center">
            <SuccessMark />

            <StaggerItem className="tw:mb-6 tw:flex tw:flex-col tw:gap-1">
                <HtmlText as="h2" className={TITLE_CLASS} translationKey="emailVerification.business.title.1" />
                <HtmlText as="h2" className={TITLE_CLASS} translationKey="emailVerification.business.title.2" />
            </StaggerItem>

            <StaggerItem className="tw:w-full tw:rounded-md tw:bg-[#FFF8E8] tw:px-5 tw:py-4">
                <HtmlText
                    className="verification-note tw:!m-0 tw:!text-[16px] tw:!font-[600] tw:!leading-relaxed tw:!text-[#282828]"
                    translationKey="emailVerification.business.notes"
                />
            </StaggerItem>

            <StaggerItem className="tw:my-8 tw:h-px tw:w-full tw:bg-[#EFEFEF]" aria-hidden="true" />

            <StaggerItem className="tw:mb-8">
                <HtmlText
                    className="tw:!m-0 tw:!text-[14px] tw:!leading-relaxed tw:!text-[#4E4E4E]"
                    translationKey="emailVerification.business.contact"
                />
            </StaggerItem>

            <StaggerItem className="tw:flex tw:w-full tw:justify-center">
                <motion.button
                    type="button"
                    className={GOLD_BUTTON_CLASS}
                    onClick={() => history.push("/")}
                    whileHover={pressable.hover}
                    whileTap={pressable.tap}
                >
                    {t("emailVerification.business.button")}
                </motion.button>
            </StaggerItem>
        </Stagger>
    );
};

// Individual users log in from here: a link to /login on mobile, the login
// popup on larger screens (same behaviour as before).
export const IndividualVerified = ({ onOpenLogin }) => {
    const { t } = useTranslation();

    return (
        <Stagger onMount stagger={0.08} className="tw:flex tw:flex-col tw:items-center tw:text-center">
            <SuccessMark />

            <StaggerItem className="tw:mb-8 tw:flex tw:flex-col tw:gap-1">
                <HtmlText as="h2" className={TITLE_CLASS} translationKey="emailVerification.individual.title.1" />
                <HtmlText as="h2" className={TITLE_CLASS} translationKey="emailVerification.individual.title.2" />
            </StaggerItem>

            <StaggerItem className="tw:flex tw:w-full tw:justify-center">
                <motion.a
                    href="/login"
                    role="button"
                    className={`${GOLD_BUTTON_CLASS} tw:md:!hidden`}
                    whileTap={pressable.tap}
                >
                    {t("emailVerification.individual.button")}
                    <EmailIcon color="#FFFFFF" />
                </motion.a>
                <motion.button
                    type="button"
                    className={`${GOLD_BUTTON_CLASS} tw:!hidden tw:md:!inline-flex`}
                    onClick={onOpenLogin}
                    whileHover={pressable.hover}
                    whileTap={pressable.tap}
                >
                    {t("emailVerification.individual.button")}
                    <ArrowRightIcon color="#FFFFFF" size={20} />
                </motion.button>
            </StaggerItem>
        </Stagger>
    );
};

// ─── Error ───────────────────────────────────────────────────────────────────

export const VerificationFailed = () => (
    <div className="tw:py-10">
        <h2 className={TITLE_CLASS}>Validating Error</h2>
    </div>
);
